type SignalMeta = {
  label: string;
  dtype: 'float32' | 'int16' | 'int8' | 'uint8';
  stride: number;
  samples: number;
  offset: number;
  length: number;
  scale?: number;
  value_offset?: number;
  shuffled?: boolean;
};

type SignalBinaryHeader = {
  version: number;
  glue_version?: number;
  mapping?: Record<string, unknown>;
  signals?: SignalMeta[];
  chunk?: {
    start_epoch: number;
    count: number;
  };
  epochs?: Array<{
    epoch: number;
    mapping: Record<string, unknown>;
    signals: SignalMeta[];
  }>;
};

const BINARY_MAGIC = 'OAW1';

const textDecoder = new TextDecoder();

const readHeader = (buffer: ArrayBuffer): { header: SignalBinaryHeader; headerLength: number } => {
  const view = new DataView(buffer);
  const magic =
    String.fromCharCode(view.getUint8(0)) +
    String.fromCharCode(view.getUint8(1)) +
    String.fromCharCode(view.getUint8(2)) +
    String.fromCharCode(view.getUint8(3));

  if (magic !== BINARY_MAGIC) {
    throw new Error('Unsupported signal payload');
  }

  const headerLength = view.getUint32(4, true);
  const headerStart = 8;
  const headerBytes = new Uint8Array(buffer, headerStart, headerLength);
  const headerJson = textDecoder.decode(headerBytes);
  return { header: JSON.parse(headerJson) as SignalBinaryHeader, headerLength };
};

export const isBinarySignalUrl = (url?: string): boolean =>
  typeof url === 'string' && url.includes('.bin.gz');

const unshuffleBytes = (input: Uint8Array, itemSize: number): Uint8Array => {
  if (itemSize <= 1) return input;
  if (input.length % itemSize !== 0) return input;
  const total = input.length;
  const samples = total / itemSize;
  const output = new Uint8Array(total);
  let idx = 0;
  for (let byte = 0; byte < itemSize; byte += 1) {
    for (let i = 0; i < samples; i += 1) {
      output[i * itemSize + byte] = input[idx++];
    }
  }
  return output;
};

const readAlignedTypedArray = (
  buffer: ArrayBuffer,
  byteOffset: number,
  byteLength: number,
  dtype: SignalMeta['dtype'],
  shuffled?: boolean
) => {
  const alignment = dtype === 'float32' ? 4 : dtype === 'int16' ? 2 : 1;
  const itemSize = alignment;
  const buildTyped = (buf: ArrayBuffer, offset: number, length: number) => {
    switch (dtype) {
      case 'int16':
        return new Int16Array(buf, offset, length / 2);
      case 'int8':
        return new Int8Array(buf, offset, length);
      case 'uint8':
        return new Uint8Array(buf, offset, length);
      default:
        return new Float32Array(buf, offset, length / 4);
    }
  };

  const slice = new Uint8Array(buffer, byteOffset, byteLength);
  const bytes = shuffled ? unshuffleBytes(slice, itemSize) : slice;
  const length = bytes.byteLength;

  if (bytes.byteOffset % alignment === 0) {
    return buildTyped(bytes.buffer, bytes.byteOffset, length);
  }

  const copy = new Uint8Array(length);
  copy.set(bytes);
  return buildTyped(copy.buffer, 0, length);
};

const decodeSignals = (
  buffer: ArrayBuffer,
  dataStart: number,
  signalsMeta: SignalMeta[]
): Record<string, number[] | number[][]> => {
  const signals: Record<string, number[] | number[][]> = {};

  for (const signal of signalsMeta) {
    const byteOffset = dataStart + signal.offset;
    const values = readAlignedTypedArray(
      buffer,
      byteOffset,
      signal.length,
      signal.dtype,
      signal.shuffled
    );

    const stride = signal.stride || 1;
    const samples = signal.samples || Math.floor(values.length / stride);
    const scale = signal.scale ?? 1;
    const valueOffset = signal.value_offset ?? 0;

    if (stride === 1) {
      // Store flat numbers — eliminates ~7680 tiny single-element arrays per signal
      // per epoch that would otherwise accumulate in the heap across cached epochs.
      // Downstream code already handles both flat numbers and [min,max] arrays via
      // Array.isArray() checks in downsampleSignal and breakdownDataIntoCoords.
      const rows = new Array(samples);
      for (let i = 0; i < samples; i += 1) {
        rows[i] = (values[i] as number) * scale + valueOffset;
      }
      signals[signal.label] = rows;
    } else {
      const rows = new Array(samples);
      for (let i = 0; i < samples; i += 1) {
        const base = i * stride;
        const rawMin = values[base] as number;
        const rawMax = values[base + 1] as number;
        rows[i] = [rawMin * scale + valueOffset, rawMax * scale + valueOffset];
      }
      signals[signal.label] = rows;
    }
  }

  return signals;
};

type EpochBinaryMeta = {
  mapping: Record<string, unknown>;
  signals: SignalMeta[];
};

export type BinaryChunk = {
  buffer: ArrayBuffer;
  dataStart: number;
  epochs: Record<number, EpochBinaryMeta>;
  glue_version?: number;
  chunk?: {
    start_epoch: number;
    count: number;
  };
};

const buildEpochData = (
  buffer: ArrayBuffer,
  dataStart: number,
  signalsMeta: SignalMeta[],
  mapping: Record<string, unknown>,
  glue_version?: number
) => ({
  signal: decodeSignals(buffer, dataStart, signalsMeta),
  mapping,
  glue_version
});

export const decodeEpochBinary = (buffer: ArrayBuffer, targetEpoch?: number) => {
  const { header, headerLength } = readHeader(buffer);
  const dataStart = 8 + headerLength;

  if (header.epochs && header.epochs.length > 0) {
    const epochEntry =
      typeof targetEpoch === 'number'
        ? header.epochs.find((entry) => entry.epoch === targetEpoch) || header.epochs[0]
        : header.epochs[0];
    return buildEpochData(
      buffer,
      dataStart,
      epochEntry.signals,
      epochEntry.mapping || {},
      header.glue_version
    );
  }

  return buildEpochData(
    buffer,
    dataStart,
    header.signals || [],
    header.mapping || {},
    header.glue_version
  );
};

export const decodeEpochBinaryChunk = (buffer: ArrayBuffer): BinaryChunk => {
  const { header, headerLength } = readHeader(buffer);
  const dataStart = 8 + headerLength;
  const epochs: Record<number, EpochBinaryMeta> = {};

  if (header.epochs && header.epochs.length > 0) {
    for (const entry of header.epochs) {
      epochs[entry.epoch] = {
        mapping: entry.mapping || {},
        signals: entry.signals
      };
    }
  } else {
    epochs[0] = {
      mapping: header.mapping || {},
      signals: header.signals || []
    };
  }

  return { buffer, dataStart, epochs, glue_version: header.glue_version, chunk: header.chunk };
};

export const materializeEpochFromChunk = (chunk: BinaryChunk, epoch: number) => {
  const epochMeta = chunk.epochs[epoch];
  if (!epochMeta) return undefined;
  return buildEpochData(
    chunk.buffer,
    chunk.dataStart,
    epochMeta.signals,
    epochMeta.mapping,
    chunk.glue_version
  );
};
